/** ******************************************************* {COPYRIGHT-TOP} ***
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corporation 2016, 2025
 *
 * All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 ********************************************************** {COPYRIGHT-END} * */

import React from 'react';
import { CopyButton } from '@carbon/react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

let copyEnabled = false;
if (typeof document !== 'undefined' && document.queryCommandSupported) {
  copyEnabled = document.queryCommandSupported('copy');
}

const copyTextCurry = (element, copierRef) => () => {
  const elementNode = document.querySelector(element) ? document.querySelector(element) : copierRef?.current;
  if (!elementNode) {
    // Can't proceed
    return;
  }
  // select and copy
  const range = document.createRange();
  range.selectNodeContents(elementNode);
  const sel = window.getSelection();
  sel.removeAllRanges();
  sel.addRange(range);

  // we've at least selected the text... but
  if (copyEnabled) {
    // we can also copy straight to the clipboard
    document.execCommand('copy');
    sel.removeAllRanges();
  }
};

function Copier({ className = '', renderStyle = '', buttonText, element, copierRef }) {
  const { t } = useTranslation();
  const copyText = copyTextCurry(element, copierRef);

  if (renderStyle === 'button') {
    return (
      <span className={classNames('apiconnect-copier', className)}>
        <button type="button" tabIndex={0} className="cds--btn cds--btn--secondary" onClick={copyText}>
          {buttonText}
        </button>
      </span>
    );
  }
  return <CopyButton onClick={copyText} feedback={t('copied_to_clipboard')} iconDescription={t('copy_to_clipboard')} data-testid="copy-button" />;
}

Copier.propTypes = {
  renderStyle: PropTypes.string, // if 'button' show a button, otherwise show carbon copy button
  className: PropTypes.string, // button classname, only applies if renderStyle is 'button'
  buttonText: PropTypes.string, // text to go in button, only applies if renderStyle is 'button'
  element: PropTypes.string // element to query select to copy text from
};

export default Copier;
